
import { Component, Watch } from "vue-property-decorator";
import { fetchRemoteGlobalSettingsRevisions } from "@/lib/global-settings/revisions";
import Vue from "vue";
import {
  GlobalSettingsRevision,
  GlobalSettingsRevisionsList,
  RevisionUiItem
} from "@/types/revisions";
import RevisionsList from "@/components/Reusable/Revisions/RevisionsList.vue";
import { isEqual } from "lodash";
import { globalStore, uiStore } from "@/store/store-accessor";
import defensiveCopy from "@/util/defensive-copy";
import { breakdanceUndo } from "@/lib/undo/undo";
import { getModuleNameFromMutation } from "@/store/util";

// TODO refactor code duplication w/SelectorsPanelHistoryViewMode
@Component({
  components: { RevisionsList }
})
export default class GlobalSettingsPanelHistoryViewMode extends Vue {
  isLoading = false;
  remoteRevisions: GlobalSettingsRevisionsList | null = null;
  selectedIndex: number | null = null;
  unsubscribeFromStore = () => {};

  currentUnsavedRevision: GlobalSettingsRevision | null = null;

  async beforeMount() {
    await this.loadRemoteRevisions();

    if (globalStore.settingsHasUnsavedChangesPresent) {
      this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();

      /**
       * Without this condition, opening revisions list after Undoing/Redoing
       * revision selection will contain "Current" revision
       */
      if (this.selectedIndex === -1) {
        this.setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData();
      }
    }

    this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();

    this.unsubscribeFromStore = this.$store.subscribe(mutationPayload => {
      const moduleName = getModuleNameFromMutation(mutationPayload);

      if (
        moduleName === "global" &&
        mutationPayload.type !== "global/restoreGlobalSettingsRevision" &&
        mutationPayload.type !== "global/setSelectors" // should be ignored
      ) {
        // Undo/Redo
        if (mutationPayload.type === "global/setGlobalSettings") {
          this.currentUnsavedRevision = null;
        } else {
          this.setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData();
        }

        this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();
      }
    });
  }

  beforeDestroy() {
    this.unsubscribeFromStore();
  }

  async loadRemoteRevisions() {
    this.isLoading = true;
    this.remoteRevisions = await fetchRemoteGlobalSettingsRevisions();
    this.selectedIndex = null;
    this.isLoading = false;
  }

  recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch() {
    this.selectedIndex = this.findSelectedIndexBasedOnStoreDataMatch();
  }

  findSelectedIndexBasedOnStoreDataMatch(): number {
    const currentGlobalSettings = defensiveCopy(globalStore.globalSettings);

    return this.revisions.findIndex(revision =>
      isEqual(revision.globalSettings, currentGlobalSettings)
    );
  }

  setCurrentStoreDataAsCurrentUnsavedRevisionIfDataIsNotEqualToMostRecentRemoteRevisionData() {
    if (this.isCurrentStoreDataTheSameAsTheMostRecentRemoteRevisionData()) {
      this.currentUnsavedRevision = null;
    } else {
      this.setCurrentUnsavedRevisionFromCurrentStoreData();
    }
  }

  isCurrentStoreDataTheSameAsTheMostRecentRemoteRevisionData() {
    if (this.remoteRevisions === null || this.remoteRevisions.length === 0) {
      return false;
    }

    const [mostRecentRemoteRevision] = this.remoteRevisions;
    const currentGlobalSettings = defensiveCopy(globalStore.globalSettings);

    return isEqual(
      mostRecentRemoteRevision.globalSettings,
      currentGlobalSettings
    );
  }

  setCurrentUnsavedRevisionFromCurrentStoreData() {
    this.currentUnsavedRevision = {
      id: -1,
      author: false,
      date: "Current",
      globalSettings: defensiveCopy(globalStore.globalSettings)
    };
  }

  get revisionUiItems(): RevisionUiItem<GlobalSettingsRevision>[] {
    return this.revisions.map(
      (
        revision: GlobalSettingsRevision,
        index: number
      ): RevisionUiItem<GlobalSettingsRevision> => ({
        isSelected: index === this.selectedIndex,
        revisionData: revision
      })
    );
  }

  get revisions(): GlobalSettingsRevision[] {
    const remoteRevisions = this.remoteRevisions ?? [];

    return this.currentUnsavedRevision
      ? [this.currentUnsavedRevision, ...remoteRevisions]
      : remoteRevisions;
  }

  onRevisionSelected(
    revisionUiItem: RevisionUiItem<GlobalSettingsRevision>,
    index: number
  ) {
    this.selectedIndex = index;

    breakdanceUndo.transaction(
      () =>
        globalStore.restoreGlobalSettingsRevision(revisionUiItem.revisionData),
      "Restore Global Settings From Revision"
    );
  }

  get unsavedChangesPresent() {
    return uiStore.unsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent")
  async updateRevisionsAfterSaving() {
    if (!uiStore.unsavedChangesPresent) {
      await this.loadRemoteRevisions();
      this.currentUnsavedRevision = null;
      this.recalculateSelectedIndexBasedOnStoreAndRevisionDataObjectsMatch();
    }
  }
}
